export interface InstrumentValidationTarget {
  instrumentId: string;
  summary: string;
  expectedFeatures: string[];
  approximationWarning: string;
}

export const validationTargets: Record<string, InstrumentValidationTarget> = {
  gayageum: {
    instrumentId: "gayageum",
    summary: "Check pluck transient, wood body modes, and nonghyeon pitch/brightness motion.",
    expectedFeatures: ["attackNoiseRatio", "pitchDriftCents", "highBandEnergyRatio", "formantPeakCandidates"],
    approximationWarning: "Gayageum synthesis is a browser-safe plucked-string approximation."
  },
  erhu: {
    instrumentId: "erhu",
    summary: "Check sustained bow energy, python skin membrane formants, and rou xian modulation.",
    expectedFeatures: ["decaySlope", "formantPeakCandidates", "vibratoDepthCentsEstimate"],
    approximationWarning: "Erhu synthesis approximates bowed friction and membrane formants without samples."
  },
  oud: {
    instrumentId: "oud",
    summary: "Check risha transient, bowl low-mid body, and course beating/roughness.",
    expectedFeatures: ["attackNoiseRatio", "lowBandEnergyRatio", "roughnessOrBeatEstimate"],
    approximationWarning: "Oud synthesis approximates course coupling and regional body response."
  },
  sitar: {
    instrumentId: "sitar",
    summary: "Check jawari high-band energy and delayed sympathetic decay tail.",
    expectedFeatures: ["highBandEnergyRatio", "decaySlope", "partialEnergyRatios"],
    approximationWarning: "Sitar jawari and taraf strings are spectral approximations."
  },
  daegeum: {
    instrumentId: "daegeum",
    summary: "Check cheong high-band threshold response, bamboo bore, and breath noise.",
    expectedFeatures: ["highBandEnergyRatio", "spectralFlatness", "formantPeakCandidates"],
    approximationWarning: "Daegeum cheong and bore behavior are approximate Web Audio models."
  },
  ney: {
    instrumentId: "ney",
    summary: "Check breath ratio, edge brightness, and regional embouchure difference.",
    expectedFeatures: ["spectralFlatness", "highBandEnergyRatio", "pitchDriftCents"],
    approximationWarning: "Ney regional profiles are source-review pending approximations."
  },
  janggu: {
    instrumentId: "janggu",
    summary: "Check low/high head spectral center separation and stroke transient differences.",
    expectedFeatures: ["spectralCentroidHz", "transientDensity", "lowBandEnergyRatio"],
    approximationWarning: "Janggu head and stroke synthesis is an approximate modal model."
  },
  tabla: {
    instrumentId: "tabla",
    summary: "Check dayan modal ratios and bayan pressure pitch drift.",
    expectedFeatures: ["partialEnergyRatios", "pitchDriftCents", "lowBandEnergyRatio"],
    approximationWarning: "Tabla syahi and bayan pressure are approximate modal membrane synthesis."
  },
  mbira: {
    instrumentId: "mbira",
    summary: "Check 5f/14f tine partials and delayed deze buzz.",
    expectedFeatures: ["partialEnergyRatios", "attackNoiseRatio", "transientDensity"],
    approximationWarning: "Mbira tine and deze buzz synthesis is an approximate model."
  },
  kora: {
    instrumentId: "kora",
    summary: "Check rank pan, calabash body, and thumb/index attack color.",
    expectedFeatures: ["lowBandEnergyRatio", "attackNoiseRatio", "decaySlope"],
    approximationWarning: "Kora rank and calabash synthesis is source-review pending."
  },
  yidaki_didgeridoo: {
    instrumentId: "yidaki_didgeridoo",
    summary: "Check moving formant peaks and continuous lip-reed drone.",
    expectedFeatures: ["formantPeakCandidates", "roughnessOrBeatEstimate", "lowBandEnergyRatio"],
    approximationWarning: "Traditional Yidaki remains an educational approximation with cultural protocol warning."
  },
  gamelan_saron: {
    instrumentId: "gamelan_saron",
    summary: "Check inharmonic bronze partials, ombak beat, and keyup damping.",
    expectedFeatures: ["inharmonicityEstimate", "roughnessOrBeatEstimate", "decaySlope"],
    approximationWarning: "Gamelan tuning and ombak are ensemble-specific approximations."
  }
};

export function getValidationTarget(instrumentId: string): InstrumentValidationTarget | undefined {
  return validationTargets[instrumentId];
}
